import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Sparkles } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { SpaCardGrid } from "@/components/spa/SpaCard";
import EmptyState from "@/components/states/EmptyState";
import { SpaGridSkeleton } from "@/components/states/LoadingSkeletons";
import api from "@/lib/api";

function prettyLocality(slug) {
  return slug.split("-").map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(" ");
}

export default function SeoLocality({ slugOverride }) {
  const params = useParams();
  const locality = slugOverride || params["*"] || "";
  const localityName = prettyLocality(locality);
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = `Best Spas in ${localityName} - Pune Spa Guide`;
    setData(null);
    api.get(`/seo/locality/${encodeURIComponent(localityName)}`).then((r) => setData(r.data)).catch(() => setData({ items: [], faq: [] }));
  }, [locality, localityName]);

  return (
    <div data-testid="seo-locality-page">
      {/* HERO */}
      <section className="hero-gradient noise-overlay">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[rgba(201,168,106,0.4)] text-xs text-[#7A5A22] mb-4">
            <MapPin size={14} /> {localityName}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl text-[#111827] tracking-tight">
            Best Spas in <span className="text-[#1F4E3D]">{localityName}</span>
          </h1>
          <p className="mt-3 text-base text-[#4B5563] max-w-2xl leading-relaxed">
            Discover the finest luxury spas, ayurvedic centres, Thai massage, and wellness retreats in {localityName}. Verified listings, real reviews, and direct contact — all on Pune Spa Guide.
          </p>
          <div className="mt-5 flex items-center gap-6 text-sm">
            <span className="flex items-center gap-1"><Sparkles size={14} className="text-[#C9A86A]"/> {data?.count || 0} spas listed</span>
          </div>
        </div>
      </section>

      {/* LISTINGS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-heading text-2xl mb-6">Spas in {localityName}</h2>
        {data === null ? <SpaGridSkeleton count={6} /> : data.items.length === 0 ? (
          <EmptyState title={`No spas listed in ${localityName} yet`} description="Check back soon or browse nearby areas." />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.items.map((s) => <SpaCardGrid key={s.id} spa={s} />)}
          </div>
        )}
      </section>

      {/* FAQ */}
      {data?.faq && data.faq.length > 0 && (
        <section data-testid="seo-locality-faq" className="section-wash">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <h2 className="font-heading text-2xl mb-6 text-center">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {data.faq.map((f, i) => (
                <AccordionItem key={f.q || `faq-${i}`} value={`item-${i}`} className="bg-white rounded-2xl border border-[#E7DDCF] px-4">
                  <AccordionTrigger className="text-left text-sm font-medium hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-[#4B5563]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}
    </div>
  );
}
